import { enableProdMode, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';

import {
  AppIdleService,
  AppInitService,
  AppInstanceService,
  AppState,
  AuthInterceptor,
  CoreModule,
  CustomBsModalServiceService,
  ErrorsModule,
  ExternalUrlRedirectResolver,
  IDLE_CONFIG,
  liveEngageTagProvider,
  LoginState,
  LogoutModule,
  logrocketInitialization,
  logrocketLogSession,
  OktaAuthConfig,
  PipeModule,
  SessionModule,
  SessionRefreshFactory,
  SmartauctionFrontendMicroLibraryModule,
  translocoAppConfig,
  UtilityService,
  WebsocketService,
  WhiteLabelTokenService,
  windowProvider,
  getAuthConfigUrl,
  getAuthConfigHeader,
  isCookieConsentEnabledByUrl,
  hasPerformanceConsent
} from 'smartauction-frontend-micro-library';
import LogRocket from 'logrocket';
import SockJS from 'sockjs-client';
import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { NgxPrintModule } from 'ngx-print';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NgxMaskModule } from 'ngx-mask';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgxSpinnerModule } from 'ngx-spinner';
import { DeviceDetectorService } from 'ngx-device-detector';
import { StorageOption, withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { AccordionModule } from 'ngx-bootstrap/accordion';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClipboardModule } from 'ngx-clipboard';
import { CommonModule } from '@angular/common';
import { routes } from './app/app.routes';
import { provideAnimations } from '@angular/platform-browser/animations';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { AssetsModule, RemarketingUiComponentsLibModule } from 'remarketing-ui-components-lib';
import { PendingChangesGuard } from './app/guards/pending-changes.guard';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { BsModalService } from 'ngx-bootstrap/modal';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import {
  AdvancedFiltersState,
  AmbassadorState,
  AnalyticsState,
  AuctionItemState,
  AuctionService,
  AuctionSummaryArbitrationState,
  AuctionSummaryFilterState,
  AuctionSummaryState,
  AuthenticationState,
  AuthGuard,
  AuthService,
  BackLinkState,
  BidListToggleState, BookProviderService,
  BuyVdpState,
  CarfaxState,
  ConfigurationService,
  ConfigurationState,
  DashboardState,
  DetailsState,
  EditPostingState,
  FinanceState, InventoryManagementState,
  InventoryService,
  LastBidListViewState,
  LaunchDarklyService,
  LeadBuyerState,
  LoggerService,
  OfferListState,
  OffersState,
  OrganizationService,
  OrganizationState,
  QuestionsState,
  RemarketingAngularLibraryModule,
  SearchBarState,
  SearchCarouselState,
  SearchFiltersState,
  SearchState,
  SellConfigurationState,
  SellSearchState,
  SellState,
  SellToggleState,
  SharedListToggleState,
  TransportationHubState,
  TransportationState,
  UploadInServiceVehiclesState,
  UserProfileState,
  VDPConfigurationState,
  VehicleProcessingState,
  VehicleToggleState,
  localProxyInterceptor
} from 'remarketing-angular-library';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH, OktaAuthConfigService, OktaAuthGuard, OktaAuthModule, OktaConfig } from '@okta/okta-angular';
import allytm from '@allysf/allytm';
import { provideRouter, RouterLink, RouterModule, withInMemoryScrolling } from '@angular/router';
import { OfferListToggleState } from './search/states/offer-list-toggle.state';
import { provideTranslocoLocale } from '@jsverse/transloco-locale';
import { MatNativeDateModule } from '@angular/material/core';
import { WebSocketProxy, initializeProxySystem } from './app/services/websocket-proxy/websocket-proxy';

/**
 * The data-test-id the library puts on the iframe it loads okta's hosted sign-in page into on
 * /login. Coupled to the library's template on purpose -- see hoistOutOfSignInFrame.
 */
const SIGN_IN_FRAME_TEST_ID = 'app-login-e-hosted-widget-frame';

/**
 * Lifts this document out of the sign-in frame when it finds itself rendering inside it.
 *
 * Okta's hosted sign-in page runs in an iframe on /login, and the links on it navigate in place:
 * clicking "Forgot username?" loads /login/forgot-username *inside* that frame, so this app comes
 * up nested in the copy of itself already wrapped around it -- two headers, two footers, the inner
 * one scrolling in a box.
 *
 * Okta's page cannot fix this from in there. Chrome only lets a frame navigate a descendant, a
 * same-origin frame, its opener, or the top frame, and that page is cross origin with the frame
 * holding it, so target="_parent" is refused outright: "Unsafe attempt to initiate navigation ...
 * neither same-origin with the target, nor is it the target's parent or opener". The refusal only
 * bites when the app is itself framed by a third party host -- Stockwave -- because then the app is
 * not the top frame either, so the top-frame exemption does not apply.
 *
 * This document *is* same origin with that frame, so it is allowed to do what okta's page was
 * refused. window.frameElement is exactly the right test for it: the DOM hands it over only when
 * the containing frame is same origin, which is the same condition that makes the navigation legal.
 * A third party framing the app -- supported, and what Stockwave does -- reads null and is left
 * alone, as is the ordinary unframed case.
 *
 * Assign rather than replace: this came from a link click, so Back should return to the login page
 * the way it would have unframed.
 *
 * Returns whether the hoist was started, so this document can decline to boot an app it is about to
 * navigate away from.
 */
function hoistOutOfSignInFrame(): boolean {
  const frame = window.frameElement;
  if (!frame || frame.getAttribute('data-test-id') !== SIGN_IN_FRAME_TEST_ID) {
    return false;
  }

  // Logged as the path, never the whole href: this is also the frame okta posts the authorization
  // code into, so on the failure mode where okta redirects the frame instead of posting, the query
  // string here carries a code -- and a code in the console is a credential in the console.
  console.log('[SignIn] nested inside the sign-in frame, hoisting to the parent:', window.location.pathname);

  // The navigation, by contrast, has to carry the whole href. Query string included: the parent is
  // taking this document's place and needs everything that addressed it.
  window.parent.location.href = window.location.href;
  return true;
}

const nestedInSignInFrame = hoistOutOfSignInFrame();

AuthService.setup();

function oktaAuthInitializer(configService: OktaAuthConfigService, oktaAuthConfig: OktaAuthConfig): () => void {
  return () => {
    const oktaAuth = new OktaAuth(oktaAuthConfig.config);
    const moduleConfig: OktaConfig = { oktaAuth };
    configService.setConfig(moduleConfig);
  };
}

// Web Worker instance for websocket handling
let websocketWorker: Worker | null = null;
let isWorkerAvailable = false;
let workerFailureCount = 0;
const MAX_WORKER_RETRIES = 2;

// SockJS transport options for fallback
const SOCKJS_TRANSPORTS = [
  'websocket',
  'hybi-10',
  'rfc6455',
  'hixie-76',
  'xhr-streaming',
  'xdr-streaming',
  'xhr-polling',
  'xdr-polling',
  'eventsource'
]

/**
 * Mark worker connection as failed - called when WebSocket error occurs in worker.
 * After MAX_WORKER_RETRIES failures, this triggers fallback to SockJS.
 */
export function markWorkerConnectionFailed(): void {
  workerFailureCount++;
  console.log(`[WebSocket] Worker connection failed (attempt ${workerFailureCount}/${MAX_WORKER_RETRIES})`);
  
  if (workerFailureCount >= MAX_WORKER_RETRIES) {
    console.log('[WebSocket] Max retries reached, will use SockJS fallback on next attempt');
    isWorkerAvailable = false;
  }
}

/**
 * Reset worker failure count - called when connection succeeds
 */
export function resetWorkerFailureCount(): void {
  if (workerFailureCount > 0) {
    console.log('[WebSocket] Worker connection succeeded, resetting failure count');
    workerFailureCount = 0;
  }
}

/**
 * Check if we should use SockJS fallback
 */
function shouldUseSockJSFallback(): boolean {
  return !isWorkerAvailable || workerFailureCount >= MAX_WORKER_RETRIES;
}

/**
 * Creates a SockJS connection as fallback when Web Worker is not available or has failed.
 */
function createSockJSFallback() {
  console.log('[WebSocket] Using SockJS fallback with transports:', SOCKJS_TRANSPORTS);
  const options = { transports: SOCKJS_TRANSPORTS };
  return new SockJS('/api/websocket/remarketing', undefined, options);
}

function initWebsocketWorker(): Worker | null {
  if (typeof Worker === 'undefined') {
    console.error('[WebSocket] Web Workers are not supported in this environment');
    return null;
  }

  if (!websocketWorker) {
    try {
      websocketWorker = new Worker(
        new URL('./app/workers/websocket/websocket.worker', import.meta.url),
        { type: 'module' }
      );
      console.log('[WebSocket] Worker created successfully:', websocketWorker);

      // Initialize the proxy system with the worker
      initializeProxySystem({
        worker: websocketWorker,
        onLog: (msg) => WebsocketService.log(msg),
        onError: () => markWorkerConnectionFailed(),
        onOpen: () => resetWorkerFailureCount()
      });

      // Mark worker as available
      isWorkerAvailable = true;

      // Handle worker errors
      websocketWorker.onerror = (error) => {
        console.error('[WebSocket] Worker error:', error);
      };

      // Initialize the worker with environment config
      const initPayload = {
        reconnectDelay: environment.websocket.reconnectDelay,
        reconnectInterval: environment.websocket.reconnectInterval,
        heartbeatIncoming: environment.websocket.heartbeatIncoming,
        heartbeatOutgoing: environment.websocket.heartbeatOutgoing,
        isEnabled: environment.websocket.isEnabled
      };
      websocketWorker.postMessage({ type: 'init', payload: initPayload });

      WebsocketService.log('WebSocket worker created and initialization sent');
    } catch (error) {
      console.error('[WebSocket] Failed to create worker:', error);
      return null;
    }
  }
  return websocketWorker;
}

function websocketInitConfig() {
  if (environment.websocket.workerEnabled) {
    // Initialize the web worker early (before any connections are made)
    initWebsocketWorker();
  }

  const websocketConfig: any = {
    reconnectDelay: environment.websocket.reconnectDelay,
    reconnectInterval: environment.websocket.reconnectInterval,
    heartbeatIncoming: environment.websocket.heartbeatIncoming,
    heartbeatOutgoing: environment.websocket.heartbeatOutgoing,
    webSocketFactory: () => {
      if (environment.websocket.workerEnabled && !shouldUseSockJSFallback()) {
        console.log(`[WebSocket] Using Web Worker proxy (failure count: ${workerFailureCount}/${MAX_WORKER_RETRIES})`);
        const proxy = new WebSocketProxy();
        proxy.connect();
        return proxy as WebSocket;
      } else {
        return createSockJSFallback();
      }
    },
    beforeConnect: client => {
      if (!environment.websocket.isEnabled) {
        client.deactivate();
      }
    },
    debug: msg => {
      WebsocketService.log(msg);
    }
  };
  return websocketConfig;
}

const logRocketSanitizeObj = {
  launchDarklyKey: environment.launchDarklyKey
};

// skipped while hoisting: this document is about to be replaced, so a session started here is a
// duplicate of the one the parent already has -- and frameElement below is the sign-in frame, not a
// partner's, so the AuctionGenius event would be wrong as well as spurious
if(!nestedInSignInFrame && (!isCookieConsentEnabledByUrl(environment.cookieConsentEnabledDomains) || hasPerformanceConsent())){
  logrocketInitialization(logRocketSanitizeObj);
  logrocketLogSession();

  if (window.frameElement) {
    console.log('site is framed, firing custom event');
    LogRocket.track('AuctionGenius');
  } else {
    console.log('Site is not framed, not firing custom event');
  }
}

if (environment.production) {
  enableProdMode();
}

function startApplication(authConfig) {
  bootstrapApplication(AppComponent, {
    providers: [
      importProvidersFrom(
        RemarketingAngularLibraryModule.forRoot(environment),
        SmartauctionFrontendMicroLibraryModule,
        RemarketingUiComponentsLibModule,
        BrowserModule,
        RouterModule,
        RouterLink,
        ErrorsModule,
        LogoutModule,
        CommonModule,
        CoreModule.forRoot(
          {
            websocketFactory: websocketInitConfig
          },
          environment,
          allytm
        ),
        ClipboardModule,
        FormsModule,
        ReactiveFormsModule,
        OktaAuthModule,
        SessionModule,
        AssetsModule,
        AccordionModule.forRoot(),
        CollapseModule.forRoot(),
        PopoverModule.forRoot(),
        BsDropdownModule.forRoot(),
        RemarketingAngularLibraryModule.forRoot(environment),
        PipeModule.forRoot(),
        AccordionModule.forRoot(),
        DeviceDetectorService,
        NgxSpinnerModule,
        TypeaheadModule.forRoot(),
        NgIdleKeepaliveModule.forRoot(),
        NgxsLoggerPluginModule.forRoot({ disabled: true }),
        NgxsReduxDevtoolsPluginModule.forRoot(),
        NgxMaskModule.forRoot(),
        MatSlideToggleModule,
        NgxPrintModule,
        MatNativeDateModule,
        InventoryService,
        AuctionService,
        OrganizationService,
        BookProviderService
      ),
      provideStore(
        [
          AppState,
          ConfigurationState,
          LoginState,
          UserProfileState,
          OrganizationState,
          AuthenticationState,
          FinanceState,
          LeadBuyerState,
          AnalyticsState,
          BackLinkState,
          VDPConfigurationState,
          CarfaxState,
          OfferListState,
          TransportationState,
          TransportationHubState,
          AuctionSummaryState,
          VehicleProcessingState,
          AmbassadorState,
          SearchState,
          AdvancedFiltersState,
          OfferListToggleState,
          SellToggleState,
          SellState,
          SellSearchState,
          AuctionItemState,
          DetailsState,
          OffersState,
          QuestionsState,
          SearchFiltersState,
          SellConfigurationState,
          SearchBarState,
          SearchCarouselState,
          VehicleToggleState,
          DashboardState,
          BuyVdpState,
          LastBidListViewState,
          SharedListToggleState,
          BidListToggleState,
          AuctionSummaryArbitrationState,
          AuctionSummaryFilterState,
          EditPostingState,
          UploadInServiceVehiclesState,
          InventoryManagementState
        ],
        withNgxsStoragePlugin({
          storage: StorageOption.SessionStorage,
          keys: [
            'appState',
            'authentication',
            'configuration',
            'login',
            'org',
            'user',
            'vdpConfiguration',
            'leadBuyer',
            'analytics',
            'sellConfiguration',
            'backLink',
            'carfax',
            'transportation',
            'transportationHub',
            'bsoConfiguration',
            'auctionSummary',
            'auctionSummaryFilters',
            'search',
            'advancedFilters',
            'sellToggle',
            'offerListToggle',
            'offerList',
            'sharedListToggle',
            'bidListToggle',
            'vehicleToggle',
            'editPosting',
            'ambassador',
            'inventoryManagement',
          ]
        })
      ),
      provideRouter(routes, withInMemoryScrolling({scrollPositionRestoration: 'enabled'})),
      {
        provide: 'externalUrlRedirectResolver',
        useClass: ExternalUrlRedirectResolver
      },
      {
        provide: OktaAuthConfig,
        useValue: authConfig
      },
      {
        provide: OKTA_AUTH,
        deps: [OktaAuthConfigService],
        useFactory: (oktaAuthConfigService: OktaAuthConfigService) => {
          return oktaAuthConfigService.getConfig().oktaAuth;
        }
      },
      {
        provide: 'OKTA_AUTH_CONFIG',
        deps: [OktaAuthConfig, LoggerService],
        useFactory: (oktaConfig: OktaAuthConfig, loggerService: LoggerService) => {
          oktaConfig.config.tokenManager = {
            secure: true,
            storage: 'sessionStorage',
            renew: false
          };
          oktaConfig.config.redirectUri = location.origin + '/auth-user/callback';
          loggerService.log('* Oktaconfig', oktaConfig.config);
          return { ...oktaConfig.config };
        }
      },
      provideAppInitializer(() => {
        const initializerFn = (SessionRefreshFactory)(inject(ConfigurationService), inject(TranslocoService), inject(WhiteLabelTokenService), inject(LaunchDarklyService));
        return initializerFn();
      }),
      provideAppInitializer(() => {
        const initializerFn = (oktaAuthInitializer)(inject(OktaAuthConfigService), inject(OktaAuthConfig));
        return initializerFn();
      }),
      AppInitService,
      AppIdleService,
      AppInstanceService,
      UtilityService,
      {
        provide: BsModalService,
        useClass: CustomBsModalServiceService
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthInterceptor,
        multi: true
      },
      WhiteLabelTokenService,
      AuthGuard,
      windowProvider,
      OktaAuthGuard,
      PendingChangesGuard,
      liveEngageTagProvider,
      {
        provide: 'IDLE_CONFIG',
        useValue: IDLE_CONFIG
      },
      provideAnimations(),
      provideHttpClient(
        ...(environment.useLocalProxy ? [withInterceptors([localProxyInterceptor])] : []),
        withInterceptorsFromDi()
      ),
      provideTransloco(translocoAppConfig),
      provideTranslocoLocale()
    ]
  }).catch(err => console.error(err));
}


if (nestedInSignInFrame) {
  // deliberately nothing. The parent is on its way to this same URL, and this document goes away
  // with the frame when it lands, so booting an app here only to tear it down would spend the auth
  // config fetch and a whole bootstrap on a page nobody sees.
  //
  // What still runs above this is AuthService.setup() and enableProdMode(), and both are meant to:
  // setup is a lazy singleton whose constructor assigns an empty array, and enableProdMode sets a
  // flag. Neither touches the network, storage or shared state, so guarding them would buy nothing
  // and would put an ordering difference between this path and the normal one. The two blocks that
  // *are* skipped are the ones that cost something -- this bootstrap, and the LogRocket session.
} else if (!sessionStorage.getItem('authConfig')) {
  fetch(getAuthConfigUrl(environment), getAuthConfigHeader(environment)).then(async res => {
    const authConfig = new OktaAuthConfig(await res.json());
    sessionStorage.setItem('authConfig', JSON.stringify(authConfig));
    startApplication(authConfig);
  });
} else {
  const authConfig = JSON.parse(sessionStorage.getItem('authConfig'));
  startApplication(authConfig);
}
